import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { repositoryAPI } from '../../services/api';
import { useConfirm } from '../../contexts/ConfirmContext';
import toast from '../../services/toast';

/**
 * Sync, code index, delete, and DORA template download actions for repository detail.
 */
export function useRepositoryActions({
  id,
  repository,
  repoQuery,
  refetchTechStack,
  canCancelSync,
}: any) {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [codeIndexTriggering, setCodeIndexTriggering] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSyncingNow = syncing || !!repository?.isSyncing;

  const handleSync = async () => {
    setSyncing(true);
    setSyncStatus('Syncing repository...');

    try {
      const data = await repositoryAPI.sync(id);
      if (data.cancelled) {
        setSyncStatus('Sync stopped.');
      } else {
        setSyncStatus('Sync complete!');
      }

      await repoQuery.refetch();
      await refetchTechStack();

      setTimeout(() => {
        setSyncStatus('');
      }, 3000);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to sync repository');
      setSyncStatus('');
    } finally {
      setSyncing(false);
    }
  };

  const handleStopSync = async () => {
    try {
      await repositoryAPI.cancelSync(id);
      setSyncStatus('Sync stopped.');
      await repoQuery.refetch();
      setTimeout(() => setSyncStatus(''), 3000);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to stop sync');
    }
  };

  const handleTriggerCodeIndex = async () => {
    if (!id || !repository?.projectId || repository.disconnectedAt) {
      toast.error('Connect this repository to a project before indexing code.');
      return;
    }
    setCodeIndexTriggering(true);
    try {
      await repositoryAPI.triggerCodeIndex(id);
      toast.success('Code indexing queued. Status updates automatically while running.');
      await repoQuery.refetch();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to queue code indexing');
    } finally {
      setCodeIndexTriggering(false);
    }
  };

  const handleDelete = () => {
    if (!repository) return;

    confirm({
      title: 'Disconnect repository',
      message: `Disconnect "${repository.fullName}" from this project? Historical commits, pull requests, and other synced data stay in your organisation; the repo moves to the unassigned pool until you connect it again.`,
      confirmLabel: 'Disconnect',
      onConfirm: async () => {
        try {
          setDeleting(true);
          await repositoryAPI.delete(id);
          navigate('/repositories');
        } catch (err) {
          toast.error((err as Error).message || 'Failed to delete repository');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleDownloadDoraLabelsTemplate = async () => {
    if (!id) return;
    try {
      await repositoryAPI.downloadDoraLabelsTemplate(id);
      toast.success('Download started');
    } catch (err) {
      toast.error((err as Error).message || 'Download failed');
    }
  };

  return {
    syncing,
    syncStatus,
    codeIndexTriggering,
    deleting,
    isSyncingNow,
    canCancelSync,
    handleSync,
    handleStopSync,
    handleTriggerCodeIndex,
    handleDelete,
    handleDownloadDoraLabelsTemplate,
  };
}
